import { useCallback, useEffect, useState } from 'react';
import { t } from '@reos/i18n';
import { APPROVALS_TEXTS, type ApprovalStatus } from '../lib/texts.js';
import { decideMcpApproval, listMcpApprovals, type McpApprovalItem } from '../lib/mcp-approvals-api.js';
import { approvalSentence } from '../lib/mcp-approval-sentence.js';
import { PageHead } from '../components/page-head.js';
import { ReportReleaseQueue } from './report-release-queue.js';
import { CorrespondenceQueue } from './correspondence-queue.js';
import { ExportApprovalQueue } from './export-approval-queue.js';

/**
 * Schvalovani MCP tool callu (krok M1 bod 3). Seznam i rozhodnuti jde na
 * `/v1/mcp/approvals` — tabulku `mcp_approvals` pod RLS tenantu. Zadny text
 * tady nesmi byt natvrdo (CLAUDE.md pravidlo 5), vsechno jde z `lib/texts.ts`.
 *
 * Krok UXK3 bod 2 — misto tabulky ID/Nástroj/Stav jsou karty s lidskou vetou
 * ("Asistentka chce založit zakázku …", `lib/mcp-approval-sentence.ts`) a
 * vyber stavu jsou zalozky (vzor `.rh-a2-tabs`), ne select. Zalozka „Vše"
 * davá cekajici pozadavky NAHORU, i kdyz jsou mezi uz rozhodnutymi.
 *
 * Krok A5: nad schvalovanim nastroju je jeste fronta „Ke schvaleni" - hotove
 * reporty, ktere ceka uvolneni zakaznikovi (`report-release-queue.tsx`).
 * Zadani A5 rika „stavajici Schvaleni", takze to neni nova obrazovka ani nova
 * polozka v menu - je to sekce na te, ktera uz existuje.
 *
 * Krok A4: tentyz princip pro DOTAZY - navrhy e-mailu spravcum siti, SVJ
 * a obcim, ktere ceka schvaleni clovekem (`correspondence-queue.tsx`). Bez
 * rozhodnuti cloveka neodchazi jmenem kancelare nic.
 */

export type ApprovalItem = McpApprovalItem;

type StatusTab = 'all' | ApprovalStatus;

const STATUS_TABS: readonly StatusTab[] = ['all', 'pending', 'approved', 'rejected', 'executed'];

/**
 * Krok UXF4 bod 8 (nalez #29): „Tri samostatne hlavni nadpisy" — obrazovka
 * mela ctyri fronty pod sebou, kazda s vlastnim `h1`. Ted je nadpis JEDEN
 * (`PageHead`), fronty jsou oddily a mezi nimi se prepina FILTREM TYPU.
 *
 * ROZHODNUTI UXF4-SCHVALENI: vychozi zalozka je „Vše". Zacit na jedne fronte
 * by schovalo cekajici polozky ostatnich — manazer by o nich nevedel, dokud
 * si na zalozku nekliknne. Filtr tedy ZUZUJE, neskryva.
 */
type TypeTab = 'all' | 'reports' | 'inquiries' | 'tools';

const TYPE_TABS: readonly TypeTab[] = ['all', 'reports', 'inquiries', 'tools'];

function statusLabel(status: string): string {
  return (APPROVALS_TEXTS.statuses as Record<string, string | undefined>)[status] ?? status;
}

function tabLabel(tab: StatusTab): string {
  return tab === 'all' ? APPROVALS_TEXTS.filterAll : statusLabel(tab);
}

/**
 * Krok UXF5 bod 4 (nalez A5) — KRATKE NAZVY FILTRU. Na 390 px konci posledni
 * zalozka useknuta („Akce as…", dukazni snimek E19), protoze „Akce asistenta"
 * je na ctyri filtry v jedne rade prilis dlouha. Zalozka proto nese kratky
 * nazev, PLNY nazev zustava pro ctecku (`aria-label` oddilu) — zkraceni je
 * tedy popisek, ne ztrata informace.
 */
function typeTabLabel(tab: TypeTab): string {
  if (tab === 'all') return APPROVALS_TEXTS.filterAll;
  if (tab === 'tools') return t('webAdmin.approvals.type.toolsShort', { locale: 'cs' });
  return t(`webAdmin.approvals.type.${tab}` as const, { locale: 'cs' });
}

/** Cekajici pozadavky nahoru, poradi uvnitr skupiny zustava (API je radi od nejnovejsiho). */
function pendingFirst(items: readonly McpApprovalItem[]): McpApprovalItem[] {
  return [...items].sort((a, b) => Number(a.status !== 'pending') - Number(b.status !== 'pending'));
}

function requestedAtText(iso: string): string {
  return new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ApprovalsPage() {
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusTab>('pending');
  const [typeFilter, setTypeFilter] = useState<TypeTab>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** `null` = fronta jeste neodpovedela (viz `queuesAnswered`). */
  const [queueCounts, setQueueCounts] = useState<{
    readonly reports: number | null;
    readonly inquiries: number | null;
    readonly exports: number | null;
  }>({ reports: null, inquiries: null, exports: null });

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const fetched = await listMcpApprovals(statusFilter === 'all' ? undefined : statusFilter);
      setItems(statusFilter === 'all' ? pendingFirst(fetched) : [...fetched]);
    } catch (e) {
      setError(`${APPROVALS_TEXTS.errorPrefix} ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  /*
   * Krok UXF5 bod 4: fronta se nacte SAMA. Do UXF5 cekala na tlacitko
   * „Načíst", takze nadpis „Čeká na rozhodnutí (N)" by tvrdil nulu, dokud si
   * clovek nekliknne — to uz by nebyla fronta, ale formular. Tlacitko
   * zustava pro rucni obnoveni.
   */
  useEffect(() => {
    void load();
  }, [load]);

  async function decide(id: string, decision: 'approved' | 'rejected'): Promise<void> {
    try {
      await decideMcpApproval(id, decision, {
        stepUpPrompt: APPROVALS_TEXTS.stepUpPrompt,
        reason: APPROVALS_TEXTS.decisionReason,
      });
      await load();
    } catch (e) {
      setError(`${APPROVALS_TEXTS.errorPrefix} ${(e as Error).message}`);
    }
  }

  const showReports = typeFilter === 'all' || typeFilter === 'reports';
  const showInquiries = typeFilter === 'all' || typeFilter === 'inquiries';
  const showTools = typeFilter === 'all' || typeFilter === 'tools';

  /*
   * Krok UXF5 bod 4 (nalez A4 + #29) — JEDNA FRONTA. Pocty hlasi jednotlive
   * oddily (`QueueSectionProps.onCount`), protoze kazda fronta ma jine API
   * a jine prava; stranka z nich sklada JEDNO cislo v nadpisu a JEDEN
   * kontextovy prazdny stav. Druhy dotaz na tytez polozky jen kvuli cislu
   * by se s obsahem fronty rozesel.
   */
  function queueCount(kind: keyof typeof queueCounts, count: number): void {
    setQueueCounts((prev) => (prev[kind] === count ? prev : { ...prev, [kind]: count }));
  }

  const visibleCount =
    (showReports ? (queueCounts.reports ?? 0) + (queueCounts.exports ?? 0) : 0) +
    (showInquiries ? (queueCounts.inquiries ?? 0) : 0) +
    (showTools ? items.filter((item) => item.status === 'pending').length : 0);

  /*
   * Prazdny stav se ukazuje, teprve kdyz vsechny viditelne fronty odpovedely.
   * Jinak by na ne obrazovka na okamzik rekla „nic neceka" a hned se prepsala.
   */
  const queuesAnswered =
    (!showReports || (queueCounts.reports !== null && queueCounts.exports !== null)) &&
    (!showInquiries || queueCounts.inquiries !== null) &&
    (!showTools || !loading);

  return (
    <div className="p-6">
      <PageHead title={APPROVALS_TEXTS.title} subtitle={APPROVALS_TEXTS.perex} />

      {/* Bod 8: filtr typu zadosti — JEDEN hlavni nadpis, oddily pod nim.
          Krok UXF5 bod 7: pod 480 px se lista zalomi, aby posledni filtr
          nekoncil useknuty na hrane (nalez A5). */}
      <div
        className="rh-a2-tabs rh-a2-tabs--wrap mb-4 min-w-0"
        role="tablist"
        aria-label={t('webAdmin.approvals.type.label', { locale: 'cs' })}
      >
        {TYPE_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={typeFilter === tab}
            className={typeFilter === tab ? 'is-on' : ''}
            onClick={() => setTypeFilter(tab)}
            data-testid={`approvals-type-tab-${tab}`}
          >
            {typeTabLabel(tab)}
          </button>
        ))}
      </div>

      {/* Krok UXF5 bod 4: JEDEN nadpis fronty misto tri sekcnich nadpisu. */}
      <h2 className="mb-2 text-lg font-semibold" data-testid="approvals-queue-heading">
        {t('webAdmin.approvals.queueHeading', { locale: 'cs', values: { count: visibleCount } })}
      </h2>

      {showReports && <ReportReleaseQueue inQueue onCount={(n) => queueCount('reports', n)} />}
      {showInquiries && <CorrespondenceQueue inQueue onCount={(n) => queueCount('inquiries', n)} />}
      {/* Krok SR1 bod 5: export a topovani nováčka ceka na potvrzeni. */}
      {showReports && <ExportApprovalQueue inQueue onCount={(n) => queueCount('exports', n)} />}

      {showTools && (
      <section aria-label={t('webAdmin.approvals.type.tools', { locale: 'cs' })} data-testid="approvals-tools-section">
      <div className="mb-4 flex items-center justify-between">
        <div className="rh-a2-tabs min-w-0" role="tablist" aria-label={APPROVALS_TEXTS.filterLabel}>
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={statusFilter === tab}
              className={statusFilter === tab ? 'is-on' : ''}
              onClick={() => {
                setStatusFilter(tab);
                setItems([]);
              }}
              data-testid={`approvals-tab-${tab}`}
            >
              {tabLabel(tab)}
            </button>
          ))}
        </div>
        <button onClick={() => void load()} className="bg-blue-600 text-rh-on-accent px-3 py-1 rounded shrink-0" type="button">
          {APPROVALS_TEXTS.reload}
        </button>
      </div>

      {loading && <p>{APPROVALS_TEXTS.loading}</p>}
      {error !== null && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}
      {/* Krok UXF5 bod 4: prazdny stav je JEDEN pro celou frontu (nize), ne
          vlastni veta kazde vetve — tri „nic neceka" pod sebou byly presne
          ten trojity dojem, ktery re-audit vytkl. */}

      <ul className="space-y-3" data-testid="approvals-cards">
        {items.map((item) => (
          <li key={item.id} className="rh-a2-card p-4" data-testid={`approval-card-${item.id}`}>
            {/*
              Krok UXF5 bod 4: TYP polozky nese karta, ne nadpis sekce. Ve
              jedne fronte je to jediny zpusob, jak clovek pozna, o co jde —
              a zaroven duvod, proc sekcni nadpisy mohly zmizet.
            */}
            <p className="mb-1">
              <span className="rh-a2-tag rh-a2-tag--non" data-testid={`approval-type-${item.id}`}>
                {t('webAdmin.approvals.type.toolsShort', { locale: 'cs' })}
              </span>{' '}
              <span className="rh-a2-tag rh-a2-tag--non" data-testid={`approval-status-${item.id}`}>
                {statusLabel(item.status)}
              </span>
            </p>
            <p className="font-medium" data-testid={`approval-sentence-${item.id}`}>
              {approvalSentence(item)}
            </p>
            {/*
              * Bod 8 (#29): „ukazuje ID a Nástroj, nikoli lidsky popsaný
              * předmět" — predmet nese veta `approvalSentence` vys, takze
              * z radku zmizelo `requestedBy` (prvnich 8 znaku UUID). Kdo
              * pozadal, je u akci asistenta vzdy asistent pod identitou
              * uzivatele; technicke ID patri do detailu, ne do seznamu.
              */}
            <p className="text-sm text-gray-500">
              {APPROVALS_TEXTS.requestedAtLabel}{' '}
              {requestedAtText(item.requestedAt)}
              {statusFilter === 'all' && item.status !== 'pending' ? ` · ${statusLabel(item.status)}` : ''}
            </p>
            {item.status === 'pending' && (
              <div className="mt-2">
                <button
                  onClick={() => void decide(item.id, 'approved')}
                  className="rh-a2-btn rh-a2-btn--sm rh-a2-btn--primary mr-1"
                  type="button"
                  data-testid={`approval-approve-${item.id}`}
                >
                  {APPROVALS_TEXTS.approve}
                </button>
                <button
                  onClick={() => void decide(item.id, 'rejected')}
                  className="rh-a2-btn rh-a2-btn--sm rh-a2-btn--danger"
                  type="button"
                  data-testid={`approval-reject-${item.id}`}
                >
                  {APPROVALS_TEXTS.reject}
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      </section>
      )}

      {/*
        Krok UXF5 bod 4 (nalez A4) — JEDEN KONTEXTOVY PRAZDNY STAV. Veta rika,
        ktereho filtru se tyka, takze „Dotazy: nic neceka" uz nevypada jako
        „na schvaleni neni nic" (tri prazdne vety pod sebou byly puvodni vada).
      */}
      {queuesAnswered && visibleCount === 0 && error === null ? (
        <div className="rh-a2-empty" data-testid="approvals-queue-empty">
          <p className="rh-a2-empty__title">
            {t('webAdmin.approvals.queueEmpty', { locale: 'cs', values: { filter: typeTabLabel(typeFilter) } })}
          </p>
        </div>
      ) : null}
    </div>
  );
}
